import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { FreeShippingCriteria, FreeShippingCriteriaValue, SellerFreeShippingRuleView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateSellerFreeShippingRules } from "@/lib/query/core/invalidation";

type CreateFreeShippingRuleInput = {
  storeSlug: string;
  name?: string | null;
  criteria: FreeShippingCriteria;
  criteriaValue: FreeShippingCriteriaValue;
  governorateIds: string[];
};

type UpdateFreeShippingRuleInput = {
  storeSlug: string;
  ruleId: string;
  name?: string | null;
  criteria?: FreeShippingCriteria;
  criteriaValue?: FreeShippingCriteriaValue;
  governorateIds?: string[];
};

type DeleteFreeShippingRuleInput = {
  storeSlug: string;
  ruleId: string;
};

export function useCreateFreeShippingRuleMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: CreateFreeShippingRuleInput) => {
      const { storeSlug: _storeSlug, ...body } = input;
      void _storeSlug;
      return apiJsonClient<SellerFreeShippingRuleView>(
        endpoints.seller.storeFreeShippingRules,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
    },
    onSuccess: async (_data, variables) => {
      await invalidateSellerFreeShippingRules(queryClient, variables.storeSlug);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useUpdateFreeShippingRuleMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: UpdateFreeShippingRuleInput) => {
      const { storeSlug: _storeSlug, ruleId, ...body } = input;
      void _storeSlug;
      return apiJsonClient<SellerFreeShippingRuleView>(
        endpoints.seller.storeFreeShippingRuleById(ruleId),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
    },
    onSuccess: async (_data, variables) => {
      await invalidateSellerFreeShippingRules(queryClient, variables.storeSlug);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useDeleteFreeShippingRuleMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: DeleteFreeShippingRuleInput) => {
      return apiJsonClient<void>(
        endpoints.seller.storeFreeShippingRuleById(input.ruleId),
        {
          method: "DELETE",
        },
      );
    },
    onSuccess: async (_data, variables) => {
      await invalidateSellerFreeShippingRules(queryClient, variables.storeSlug);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
